import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, Mail } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

export const Hero = ({ onBookClick }) => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1760902419069-466f6f82c8b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYWNjZW50JTIwZGFyayUyMGx1eHVyeSUyMHRleHR1cmV8ZW58MHx8fHwxNzc4NzAzNDcwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#051024]/95 via-[#051024]/85 to-[#0A192F]/90" />
        <div className="absolute inset-0 bg-grid-gold opacity-40" />
      </div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="relative z-10 px-6 md:px-12 lg:px-24 pt-32 pb-24 w-full"
      >
        <div className="max-w-5xl">
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-8" data-testid="hero-overline">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
              PMP · PMO Consultant · 13 Years
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-serif font-bold text-[#F8F9FA] leading-[1.05] text-balance text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
            data-testid="hero-heading"
          >
            Deepshikha
            <br />
            <span className="italic font-medium text-[#D4AF37]">Jaiswal</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-2xl text-lg md:text-xl text-[#A8B2D1] leading-relaxed font-light"
            data-testid="hero-tagline"
          >
            Engineering excellence through disciplined project execution. Transforming PMOs, processes and
            on-time delivery for industrial and engineering organizations across four continents.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-12 flex flex-col sm:flex-row flex-wrap gap-4">
            <button
              onClick={onBookClick}
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#051024] hover:bg-[#F1D570] transition-all duration-300 px-8 py-5 text-sm font-semibold tracking-wide hover:shadow-[0_0_30px_rgba(212,175,55,0.35)]"
              data-testid="hero-book-consultation-btn"
            >
              Book a Consultation
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href="mailto:Shikha.JaiswalPMP@outlook.com"
              className="group inline-flex items-center justify-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#051024] transition-all duration-300 px-8 py-5 text-sm font-semibold tracking-wide"
              data-testid="hero-email-btn"
            >
              <Mail size={16} strokeWidth={1.75} />
              Email Me
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-3 text-[#A8B2D1] hover:text-[#D4AF37] transition-colors duration-300 px-2 py-5 text-sm font-semibold tracking-wide"
              data-testid="hero-services-btn"
            >
              Explore Services →
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            variants={fadeUp}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-10 border-t border-[#233554] pt-8 max-w-3xl"
            data-testid="hero-trust-strip"
          >
            {[
              ["35→95%", "OTD Improvement"],
              ["60%", "Faster Quoting"],
              ["13", "Years Experience"],
              ["9", "Countries Served"],
            ].map(([k, v]) => (
              <div key={v}>
                <p className="font-serif text-2xl md:text-3xl text-[#D4AF37]">{k}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A8B2D1] mt-1">{v}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ delay: 1, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A8B2D1] hover:text-[#D4AF37] transition-colors hidden md:block"
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown size={20} />
      </motion.a>
    </section>
  );
};
